import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {collection, onSnapshot, doc, getDoc, where, query, updateDoc, serverTimestamp, addDoc, deleteDoc, orderBy, getDocs, limit, setDoc } from 'firebase/firestore';
import { generateLogId, generateContactFocusId, generateNoteId } from '../utils/documentIdUtils';
import { db } from '../utils/firebase';
import OpportunityModal, { getValueRangeLabel, formatActualValue } from '../components/forms/OppModal';
import OrderModal from '../components/forms/OrderModal';
import Toast from '../components/common/Toast';
import PaginationControls from '../components/common/PaginationControls';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { getDateRange } from '../utils/dateHelpers';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { logUserAction, logEntityChanges, logEntityDeletion } from '../utils/logger';
import ContactModal from '../components/forms/ContactModal';
import CustomerModal from '../components/forms/CustomerModal';
import CustomerMatchDialog from '../components/forms/CustomerMatchDialog';
import { mergeCustomers } from '../utils/customerMergeUtils';
import { useAuth } from '../components/auth/AuthContext';
import { useGamification } from '../components/gamification/GamificationContext';
import { sanitizeString, sanitizeNumber, sanitizeDate } from '../utils/sanitization';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from '../utils/firebase';
import { uploadAttachment, deleteAttachment, subscribeToAttachments, checkAttachmentExists } from '../utils/attachmentsUtils';
import { formatDate } from '../utils/dateHelpers';
import { collectionPath } from '../utils/firestoreCollections';
import EmailComposer from '../components/forms/EmailComposer';
import LocationModal from '../components/forms/LocationModal';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { fetchTasksByEntity, fetchAllCustomerLogs } from '../utils/associationUtils';
import UnifiedNotes from '../components/common/UnifiedNotes';
import { indexDocument, partialUpdateDocument } from '../utils/typesense';
import { useAddon } from '../hooks/useAddon';
import { ADDONS } from '../utils/addons';
import RecommendationsPanel from '../components/recommendations/RecommendationsPanel';
import { useQuoting } from '../hooks/useQuoting';

const PAGE_SIZE = 25;
const LOG_PAGE_SIZE = 25;
const NOTES_PAGE_SIZE = 10;

const DEFAULT_BUSINESS_TYPES = ['Country Club', 'Management customer', 'Specialty Store', 'Department Store', 'Resort', 'Online', 'Other'];
const DEFAULT_BUSINESS_FOCUS = ['Not Specified', 'Golf', 'Tennis', 'Pickleball', 'Other'];

const CustomerDetailPage = (props) => {
    const { currentUser } = useAuth();
    const { recordActivity } = useGamification();
    const { customerid } = useParams(); // Must match route param name (lowercase)
    const customerId = customerid; // Keep as customerId for rest of component
    const navigate = useNavigate();
    const { can } = usePermissions(currentUser);
    const location = useLocation();
    const { enabled: aiAgentEnabled } = useAddon(ADDONS.AI_SALES_AGENT);
    const quoting = useQuoting(currentUser);
    const showRecommendationsTab = aiAgentEnabled && can(FEATURE_PERMISSIONS.AI_RECS_VIEW);

    const [selectedTab, setActiveTab] = useState(null);
    const activeTab = selectedTab === 'recommendations' && !showRecommendationsTab
        ? 'locations'
        : selectedTab || (showRecommendationsTab ? 'recommendations' : 'locations');

    useEffect(() => {
        const tab = new URLSearchParams(location.search).get('tab');
        setActiveTab(tab || null);
    }, [location.search, customerId]);
    const [customer, setCustomer] = useState(null);
    const [opportunities, setOpportunities] = useState([]);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editOpportunity, setEditOpportunity] = useState(null);
    const [editOrder, setEditOrder] = useState(null);
    const [toast, setToast] = useState(null);
    const [showEditContact, setShowEditContact] = useState(false);
    const [editContact, setEditContact] = useState(null);
    const [oppPage, setOppPage] = useState(1);
    const [orderPage, setOrderPage] = useState(1);
    const [logsLoading, setLogsLoading] = useState(false);
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    // Add sort state for each tab
    const [oppSortBy, setOppSortBy] = useState('opportunityNumberLabel'); // default sort by Opportunity Number
    const [oppSortDir, setOppSortDir] = useState('asc');
    const [orderSortBy, setOrderSortBy] = useState('orderNumber');
    const [orderSortDir, setOrderSortDir] = useState('desc');
    const [contactSortBy, setContactSortBy] = useState('isPrimary'); // default sort by Primary Contact
    const [contactSortDir, setContactSortDir] = useState('desc');
    const [notesSearch, setNotesSearch] = useState('');
    const [opportunitiesSearch, setOpportunitiesSearch] = useState('');
    const [ordersSearch, setOrdersSearch] = useState('');
    const [contactsSearch, setContactsSearch] = useState('');
    const [activitySearch, setActivitySearch] = useState('');
    const [logs, setLogs] = useState([]);
    const [logSearch, setLogSearch] = useState('');
    const [logPage, setLogPage] = useState(1);
    const [notesPage, setNotesPage] = useState(1);
    const [notesCount, setNotesCount] = useState(0);
    const [logsRefreshKey, setLogsRefreshKey] = useState(0); // Trigger activity log refresh
    const [customerRefreshKey, setCustomerRefreshKey] = useState(0); // Trigger customer info refresh
    const [opportunitiesRefreshKey, setOpportunitiesRefreshKey] = useState(0); // Trigger opportunities refresh
    const [ordersRefreshKey, setOrdersRefreshKey] = useState(0); // Trigger orders refresh
    const [tasksRefreshKey, setTasksRefreshKey] = useState(0); // Trigger tasks refresh
    const [contactsRefreshKey, setContactsRefreshKey] = useState(0); // Trigger contacts refresh
    const [notesRefreshKey, setNotesRefreshKey] = useState(0); // Trigger notes refresh
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [editingContactId, setEditingContactId] = useState(null);
    const [contactEdit, setContactEdit] = useState({});
    const [savingContact, setSavingContact] = useState(false);
    const [businessTypeOptions, setBusinessTypeOptions] = useState(DEFAULT_BUSINESS_TYPES);
    const [showAddBusinessType, setShowAddBusinessType] = useState(false);
    const [newBusinessType, setNewBusinessType] = useState('');
    const [contactFocusOptions, setContactFocusOptions] = useState(DEFAULT_BUSINESS_FOCUS);
    const [showAddBusinessFocus, setShowAddBusinessFocus] = useState(false);
    const [newBusinessFocus, setNewBusinessFocus] = useState('');
    const [showAddContact, setShowAddContact] = useState(false);
    const [newContact, setNewContact] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        title: '',
        businessType: '',
        focus: [],
        isPrimary: false,
        customerId: customerId,
    });
    const [showContactModal, setShowContactModal] = useState(false);
    const [contactModalData, setContactModalData] = useState(null); // null for add, contact object for edit
    const [showCustomerModal, setshowCustomerModal] = useState(false); // 2. State
    const [CustomerModalData, setCustomerModalData] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef(null);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailDraft, setEmailDraft] = useState({});

    const [showLocationModal, setShowLocationModal] = useState(false);
    const [editLocation, setEditLocation] = useState(null);

    // Customer merge state (admin only)
    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [allCustomers, setAllCustomers] = useState([]);
    const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);
    const [merging, setMerging] = useState(false);


    const openEmailModal = (contact) => {
        setEmailDraft({
            uid: currentUser?.uid,
            to: contact.email || '',
            subject: `Regarding ${customer?.customerName || 'your account'}`,
            body: `Hi ${contact.firstName || ''},\n\n`,
            from: currentUser?.email
        });
        setEmailModalOpen(true);
    };

    // Fetch customer (one-time, not real-time)
    // Refetches when customerRefreshKey changes (e.g., switching to locations tab or after editing)
    useEffect(() => {
        if (!currentUser || !customerId) return;
        setLoading(true);
        console.log('[CustomerDetailPage] Fetching customer:', customerId);
        getDoc(doc(db, collectionPath('customers'), customerId))
            .then(customerDoc => {
                console.log('[CustomerDetailPage] Customer doc exists:', customerDoc.exists());
                if (customerDoc.exists()) {
                    setCustomer({ id: customerDoc.id, ...customerDoc.data() });
                } else {
                    console.warn('[CustomerDetailPage] Customer not found:', customerId);
                    setCustomer(null);
                }
            })
            .catch(err => {
                console.error('[CustomerDetailPage] Failed to load customer:', err);
                setCustomer(null);
            })
            .finally(() => setLoading(false));
    }, [currentUser, customerId, customerRefreshKey]);

    // Fetch tasks associated with this customer
    // Uses utility that handles both associations.customerId and root-level customerId
    // Refetches when tasksRefreshKey changes (e.g., switching to tasks tab or after creating/editing task)
    useEffect(() => {
        if (!customerId) return;
        
        setTasksLoading(true);
        fetchTasksByEntity('customer', customerId)
            .then(tasksData => {
                setTasks(tasksData);
                setTasksLoading(false);
            })
            .catch(error => {
                console.error('Error fetching customer tasks:', error);
                setTasksLoading(false);
            });
    }, [customerId, tasksRefreshKey]);

    // Fetch opportunities (on-demand, not real-time) - 95% read reduction
    // Refetches when opportunitiesRefreshKey changes (e.g., switching to opportunities tab or after saving)
    useEffect(() => {
        if (!customerId) return;
        
        const fetchOpportunities = async () => {
            try {
                const oppsQ = query(
                    collection(db, collectionPath('opportunities')),
                    where('customerId', '==', customerId),
                    orderBy('startDate', 'desc'),
                    limit(PAGE_SIZE)
                );
                const snapshot = await getDocs(oppsQ);
                setOpportunities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) {
                console.error('Failed to fetch opportunities:', err);
                setOpportunities([]);
            }
        };
        
        fetchOpportunities();
    }, [customerId, opportunitiesRefreshKey]);

    // Fetch orders associated with this customer (by customer_id or apparelMagicId)
    // Refetches when ordersRefreshKey changes (e.g., switching to orders tab or after saving)
    useEffect(() => {
        if (!customerId || !customer) return;
        
        const fetchOrders = async () => {
            try {
                // orders.customer_id holds the ApparelMagic customer_id (== customer.apparelMagicId).
                // Legacy/manual orders may instead store the account number or the customer doc ID.
                const matchIds = [...new Set(
                    [customer.apparelMagicId, customer.customerAccountNumber, customer.customerId, customerId]
                        .filter(Boolean)
                        .map(String)
                )].slice(0, 10); // Firestore 'in' supports max 10 values

                if (matchIds.length === 0) {
                    setOrders([]);
                    return;
                }

                const ordersQ = query(
                    collection(db, collectionPath('orders')),
                    where('customer_id', 'in', matchIds),
                    orderBy('date_internal', 'desc'),
                    limit(100)
                );
                
                const snapshot = await getDocs(ordersQ);
                const fetchedOrders = snapshot.docs.map(doc => {
                    const data = doc.data();
                    
                    // Extract item count from order_items array (actual field from ApparelMagic)
                    const itemCount = Array.isArray(data.order_items) ? data.order_items.length : 0;
                    
                    // Extract total from 'amount' field (actual field from ApparelMagic - final total after discounts)
                    const totalAmount = parseFloat(data.amount) || 0;
                    
                    // Use 'date' field as-is (formatted as MM/DD/YYYY from ApparelMagic)
                    const displayDate = data.date || '-';
                    
                    // Use 'fulfillment_status' field (actual field from ApparelMagic)
                    const status = data.fulfillment_status || 'Unknown';
                    
                    // Extract sales rep from commissions_displayNames or commissions array
                    let salesRep = '-';
                    if (Array.isArray(data.commissions_displayNames) && data.commissions_displayNames.length > 0) {
                        salesRep = data.commissions_displayNames.join(', ');
                    } else if (Array.isArray(data.commissions) && data.commissions.length > 0) {
                        const names = data.commissions.map(c => c.displayName || c.name).filter(Boolean);
                        if (names.length > 0) {
                            salesRep = names.join(', ');
                        }
                    }
                    
                    return {
                        id: doc.id,
                        ...data, // Keep all original data
                        // Normalize fields for display using correct ApparelMagic field names
                        orderNumber: data.order_id || doc.id,
                        orderNumberNumeric: parseInt(data.order_id) || 0, // For numeric sorting
                        date: displayDate,
                        dateInternal: data.date_internal || data.date, // For proper date sorting
                        status: status,
                        total: totalAmount,
                        items: itemCount,
                        customerPO: data.customer_po,
                        customerName: data.customer_name,
                        salesRep: salesRep,
                    };
                });
                
                setOrders(fetchedOrders);
            } catch (err) {
                console.error('[CustomerDetailPage] Failed to fetch orders:', err);
                setOrders([]);
            }
        };
        
        fetchOrders();
    }, [customerId, customer, ordersRefreshKey]);

    // Fetch users (one-time, not real-time, unless you need live updates)
    useEffect(() => {
        // Always fetch all users for admin, or all for assignment
        const fetchUsers = async () => {
            try {
                const snap = await getDocs(
                    query(
                        collection(db, collectionPath('users')),
                        orderBy('firstName', 'asc'),
                        limit(500) // adjust limit as needed
                    )
                );
                setUsers(snap.docs.map(doc => ({
                    uid: doc.id,
                    ...doc.data()
                })));
            } catch (err) {
                console.error('Failed to fetch users:', err);
                setUsers([]);
            }
        };
        fetchUsers();
    }, []);

    // Fetch notes count for tab badge (unified notes collection)
    // Refetches when notesRefreshKey changes (e.g., switching to notes tab or after adding/deleting note)
    useEffect(() => {
        if (!customerId) {
            setNotesCount(0);
            return;
        }
        
        const fetchNotesCount = async () => {
            try {
                const notesQ = query(
                    collection(db, collectionPath('notes')),
                    where('associations.customerId', '==', customerId),
                    orderBy('createdAt', 'desc'),
                    limit(100) // Match UnifiedNotes maxNotes default
                );
                const notesSnap = await getDocs(notesQ);
                setNotesCount(notesSnap.size);
            } catch (err) {
                console.error('Failed to fetch notes count:', err);
                setNotesCount(0);
            }
        };
        
        fetchNotesCount();
    }, [customerId, notesRefreshKey]);

    // Fetch logs (on-demand, not real-time) - now fetches from ALL related entities
    // Includes: logsCustomer, logsOpp, logsTask, logsOrder, logsContact
    // Refetches when logsRefreshKey changes (e.g., after saving opportunity/order or switching to activity tab)
    useEffect(() => {
        if (!customerId) return;
        
        const fetchLogs = async () => {
            setLogsLoading(true);
            try {
                // Use unified fetch that gets logs from all related entities
                const allLogs = await fetchAllCustomerLogs(customerId, LOG_PAGE_SIZE);
                setLogs(allLogs);
            } catch (err) {
                console.error('Failed to fetch logs:', err);
                setLogs([]);
            } finally {
                setLogsLoading(false);
            }
        };
        
        fetchLogs();
    }, [customerId, logsRefreshKey]);

    // Refresh data when switching tabs to ensure latest information is displayed
    useEffect(() => {
        switch (activeTab) {
            case 'locations':
                setCustomerRefreshKey(prev => prev + 1);
                break;
            case 'opportunities':
                setOpportunitiesRefreshKey(prev => prev + 1);
                break;
            case 'orders':
                setOrdersRefreshKey(prev => prev + 1);
                break;
            case 'contacts':
                setContactsRefreshKey(prev => prev + 1);
                break;
            case 'tasks':
                setTasksRefreshKey(prev => prev + 1);
                break;
            case 'notes':
                setNotesRefreshKey(prev => prev + 1);
                break;
            case 'activity':
                setLogsRefreshKey(prev => prev + 1);
                break;
            default:
                break;
        }
    }, [activeTab]);

    const usersById = useMemo(() => {
        const m = new Map();
        users.forEach(u => m.set(u.uid, u));
        return m;
    }, [users]);

    // Remove date filtering, show all for this customer (optimize search)
    const filteredOpportunities = useMemo(() => {
        const q = (opportunitiesSearch || '').toLowerCase().trim();
        if (!q) return opportunities;
        return opportunities.filter(opp => {
            const rep = users.find(u => u.uid === opp.uid);
            const repName = rep
                ? `${rep.firstName || ''} ${rep.lastName || ''} ${rep.email || ''}`.toLowerCase()
                : '';
            return [
                opp.leadSource,
                opp.stage,
                String(opp.value || ''),
                opp.startDate,
                opp.dueDate,
                repName
            ]
            .filter(Boolean)
            .map(v => v.toString().toLowerCase())
            .some(v => v.includes(q));
        });
    }, [opportunities, users, opportunitiesSearch]);

    const filteredOrders = useMemo(() => {
        const q = (ordersSearch || '').toLowerCase().trim();
        if (!q) return orders;
        const customerName = (customer?.name || '').toLowerCase();
        return orders.filter(order => {
            return [
                order.orderNumber,
                order.status,
                order.date,
                order.total?.toString(),
                customerName
            ]
            .filter(Boolean)
            .map(v => v.toString().toLowerCase())
            .some(v => v.includes(q));
        });
    }, [orders, ordersSearch, customer]);

    const filteredContacts = useMemo(() => {
        const q = (contactsSearch || '').toLowerCase().trim();
        if (!q) return contacts;
        return contacts.filter(contact =>
            [
                contact.firstName,
                contact.lastName,
                contact.email,
                contact.phone,
                contact.contactPosition,
                contact.contactCustomerId,
                Array.isArray(contact.contactFocus) ? contact.contactFocus.join(', ') : contact.contactFocus,
                contact.businessType,
            ]
            .filter(Boolean)
            .map(v => v.toString().toLowerCase())
            .some(v => v.includes(q))
        );
    }, [contacts, contactsSearch]);

    const filteredLogs = useMemo(() => {
        const q = (logSearch || '').toLowerCase().trim();
        if (!q) return logs;
        return logs.filter(log =>
            (log.action || '').toLowerCase().includes(q) ||
            (log.details || '').toLowerCase().includes(q) ||
            (log.userEmail || '').toLowerCase().includes(q) ||
            (log.timestamp && log.timestamp.toDate && log.timestamp.toDate().toLocaleString().toLowerCase().includes(q))
        );
    }, [logs, logSearch]);

    // Sorting helpers
    const sortArray = (arr, sortBy, sortDir) => {
        return [...arr].sort((a, b) => {
            let aValue = a[sortBy] || '';
            let bValue = b[sortBy] || '';
            
            // Special handling for order numbers - sort numerically
            if (sortBy === 'orderNumber') {
                const aNum = a.orderNumberNumeric || 0;
                const bNum = b.orderNumberNumeric || 0;
                return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
            }
            
            // Special handling for dates - use date_internal for proper sorting
            if (sortBy === 'date') {
                const aDate = a.dateInternal || a.date || '';
                const bDate = b.dateInternal || b.date || '';
                if (aDate < bDate) return sortDir === 'asc' ? -1 : 1;
                if (aDate > bDate) return sortDir === 'asc' ? 1 : -1;
                return 0;
            }
            
            // Default string/number sorting
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const sortedOpportunities = useMemo(() => 
        sortArray(filteredOpportunities, oppSortBy, oppSortDir),
        [filteredOpportunities, oppSortBy, oppSortDir]
    );
    
    const sortedOrders = useMemo(() => 
        sortArray(filteredOrders, orderSortBy, orderSortDir),
        [filteredOrders, orderSortBy, orderSortDir]
    );
    
    const sortedContacts = useMemo(() => 
        sortArray(filteredContacts, contactSortBy, contactSortDir),
        [filteredContacts, contactSortBy, contactSortDir]
    );
    
    const sortedLogs = useMemo(() => 
        sortArray(logs, 'timestamp', 'desc'),
        [logs]
    );

    // Pagination helpers (use sorted arrays)
    const paginatedOpportunities = sortedOpportunities.slice(
        (oppPage - 1) * PAGE_SIZE,
        oppPage * PAGE_SIZE
    );
    const paginatedOrders = sortedOrders.slice(
        (orderPage - 1) * PAGE_SIZE,
        orderPage * PAGE_SIZE
    );
    const paginatedLogs = filteredLogs.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE);
    const filteredNotes = notes.filter(note =>
        (note.note || '').toLowerCase().includes(notesSearch.toLowerCase()) ||
        (note.userEmail || '').toLowerCase().includes(notesSearch.toLowerCase())
    );
    const paginatedNotes = filteredNotes.slice(
        (notesPage - 1) * NOTES_PAGE_SIZE,
        notesPage * NOTES_PAGE_SIZE
    );
    const oppTotalPages = Math.ceil(filteredOpportunities.length / PAGE_SIZE);
    const orderTotalPages = Math.ceil(filteredOrders.length / PAGE_SIZE);
    const totalLogPages = Math.ceil(filteredLogs.length / LOG_PAGE_SIZE);
    const notesTotalPages = Math.ceil(filteredNotes.length / NOTES_PAGE_SIZE);

    // Reset page to 1 when counts change
    useEffect(() => { setOppPage(1); }, [filteredOpportunities.length]);
    useEffect(() => { setOrderPage(1); }, [filteredOrders.length]);
    useEffect(() => { setLogPage(1); }, [logSearch, logs.length]);

    const handleEditContact = () => {
        // Ensure contactFocus is always an array for editing
        const cf = customer?.contactFocus;
        setContactModalData({
            ...customer,
            contactFocus: Array.isArray(cf)
                ? cf
                : (typeof cf === 'string' && cf ? cf.split(',').map(s => s.trim()).filter(Boolean) : []),
            // Optionally, map customer fields to contact fields if needed
            // e.g., firstName: customer.firstName, lastName: customer.lastName, etc.
        });
        setShowContactModal(true);
    };


    const handleSaveContact = async (form) => {
        try {
            // Normalize contactFocus to array with extra safety
            let contactFocusArr = [];
            if (Array.isArray(form.contactFocus)) {
                contactFocusArr = form.contactFocus.filter(f => f && typeof f === 'string');
            } else if (typeof form.contactFocus === 'string' && form.contactFocus) {
                contactFocusArr = form.contactFocus.split(',').map(s => s.trim()).filter(Boolean);
            } else if (form.contactFocus && typeof form.contactFocus === 'object') {
                // Handle object case
                contactFocusArr = Object.values(form.contactFocus).filter(f => f && typeof f === 'string');
            }
            
            const now = new Date().toISOString();
            const contactId = form.id || form.contactId || Date.now().toString();
            
            // Build payload for standalone contacts collection
            const payload = {
                contactId: contactId,
                contactCustomerId: customerId,
                customerId: customerId,
                customerName: customer?.customerName || '',
                firstName: String(form.firstName || ''),
                lastName: String(form.lastName || ''),
                email: String(form.email || ''),
                phone: String(form.phone || ''),
                contactPosition: String(form.title || ''),
                contactFocus: contactFocusArr,
                isPrimary: Boolean(form.isPrimary) ? '1' : '0',
                updatedAt: serverTimestamp(),
                sourceCreation: 'CRM',
            };

            const contactRef = doc(db, collectionPath('contacts'), contactId);
            const existingDoc = await getDoc(contactRef);

            if (existingDoc.exists()) {
                // Update existing contact
                const originalContact = existingDoc.data();
                await updateDoc(contactRef, payload);

                // Log the contact edit activity with detailed changes
                try {
                    const changes = [];
                    const fieldsToCheck = [
                        { key: 'firstName', label: 'First Name' },
                        { key: 'lastName', label: 'Last Name' },
                        { key: 'email', label: 'Email' },
                        { key: 'phone', label: 'Phone' },
                        { key: 'contactPosition', label: 'Title' },
                        { key: 'contactFocus', label: 'Focus' },
                        { key: 'isPrimary', label: 'Primary Contact' }
                    ];
                    fieldsToCheck.forEach(({ key, label }) => {
                        let oldValue = originalContact[key];
                        let newValue = payload[key];
                        // Normalize arrays for contactFocus
                        if (key === 'contactFocus') {
                            if (Array.isArray(oldValue)) oldValue = oldValue.join(', ');
                            if (Array.isArray(newValue)) newValue = newValue.join(', ');
                        }
                        // Normalize booleans for isPrimary
                        if (key === 'isPrimary') {
                            oldValue = oldValue === '1' || oldValue === true ? 'Yes' : 'No';
                            newValue = newValue === '1' || newValue === true ? 'Yes' : 'No';
                        }
                        if (oldValue !== newValue) {
                            changes.push(`${label} changed from "${oldValue || '-'}" to "${newValue || '-'}"`);
                        }
                    });
                    
                    const detailsMsg = changes.length > 0
                        ? `Contact ${payload.firstName} ${payload.lastName} was updated. Changes: ${changes.join('; ')}`
                        : `Contact ${payload.firstName} ${payload.lastName} was updated.`;
                    const logId = generateLogId('contact', contactId, 'edited');
                    await setDoc(doc(db, collectionPath('logsContact'), logId), {
                        contactId: contactId,
                        customerId: customerId,
                        userId: currentUser?.uid,
                        userEmail: currentUser?.email,
                        action: 'Contact Edited',
                        details: detailsMsg,
                        timestamp: serverTimestamp(),
                    });
                } catch (logErr) {
                    console.warn('Failed to log contact edit activity:', logErr);
                }
                
                // Gamification: Award points for contact update (encourages keeping contact info current)
                try {
                    await recordActivity('CONTACT_UPDATED', {
                        entityId: contactId,
                        source: 'manual',
                        oldData: originalContact,
                        entityData: payload,
                        fieldsToCheck: ['firstName', 'lastName', 'email', 'phone', 'contactPosition', 'contactFocus', 'isPrimary']
                    });
                } catch (e) { console.warn('Gamification failed:', e); }
                
                setToast({ message: 'Contact updated', type: 'success' });

                // Sync Typesense index so Contacts list reflects the change immediately
                try {
                    await partialUpdateDocument('contacts', {
                        id: contactId,
                        firstName: payload.firstName || undefined,
                        lastName: payload.lastName || undefined,
                        email: payload.email || undefined,
                        phone: payload.phone || undefined,
                        title: payload.contactPosition || undefined,
                        contactFocus: contactFocusArr.length > 0 ? contactFocusArr : undefined,
                        isPrimary: payload.isPrimary === '1' || payload.isPrimary === true,
                        customerId: customerId || undefined,
                        customerName: customer?.customerName || undefined,
                        customerAccountNumber: customer?.apparelMagicId || customer?.customerAccountNumber || undefined,
                        salesRepNames: Array.isArray(customer?.salesReps)
                            ? customer.salesReps.map(r => r.salesRepName || r.displayName).filter(Boolean)
                            : undefined,
                        createdAt: existingDoc.data()?.createdAt?.seconds != null
                            ? Math.floor(existingDoc.data().createdAt.seconds)
                            : Math.floor(Date.now() / 1000),
                    });
                } catch (tsErr) {
                    console.warn('[CustomerDetailPage] Typesense contact update failed (non-fatal):', tsErr);
                }
            } else {
                // Create new contact
                const newPayload = {
                    ...payload,
                    createdAt: serverTimestamp(),
                };
                await setDoc(contactRef, newPayload);

                // Log the contact creation activity
                try {
                    const logId = generateLogId('contact', contactId, 'created');
                    await setDoc(doc(db, collectionPath('logsContact'), logId), {
                        contactId: contactId,
                        customerId: customerId,
                        userId: currentUser?.uid,
                        userEmail: currentUser?.email,
                        action: 'Contact Created',
                        details: `Contact ${payload.firstName} ${payload.lastName} was added`,
                        timestamp: serverTimestamp(),
                    });
                } catch (logErr) {
                    console.warn('Failed to log contact creation activity:', logErr);
                }
                
                // Gamification: Award points for contact creation
                try { await recordActivity('CONTACT_CREATED'); } catch (e) { console.warn('Gamification failed:', e); }

                // Sync new contact to Typesense
                try {
                    await partialUpdateDocument('contacts', {
                        id: contactId,
                        firstName: payload.firstName || undefined,
                        lastName: payload.lastName || undefined,
                        email: payload.email || undefined,
                        phone: payload.phone || undefined,
                        title: payload.contactPosition || undefined,
                        contactFocus: contactFocusArr.length > 0 ? contactFocusArr : undefined,
                        isPrimary: payload.isPrimary === '1' || payload.isPrimary === true,
                        customerId: customerId || undefined,
                        customerName: customer?.customerName || undefined,
                        customerAccountNumber: customer?.apparelMagicId || customer?.customerAccountNumber || undefined,
                        salesRepNames: Array.isArray(customer?.salesReps)
                            ? customer.salesReps.map(r => r.salesRepName || r.displayName).filter(Boolean)
                            : undefined,
                        createdAt: Math.floor(Date.now() / 1000),
                    });
                } catch (tsErr) {
                    console.warn('[CustomerDetailPage] Typesense contact create failed (non-fatal):', tsErr);
                }

                setToast({ message: 'Contact added', type: 'success' });
            }

            setShowContactModal(false);
            setContactsRefreshKey(prev => prev + 1); // Refresh contacts list
            setLogsRefreshKey(prev => prev + 1); // Refresh activity log
        } catch (err) {
            console.error('Failed to save contact', err);
            setToast({ message: 'Failed to save contact', type: 'error' });
        }
    };

        //Logic to delete contact but button is missing for now
    const handleDeleteContact = async (contact) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;
        try {
            const contactId = contact.contactId || contact.id;
            if (!contactId) {
                setToast({ message: 'Contact ID not found', type: 'error' });
                return;
            }

            // Delete from standalone contacts collection
            const contactRef = doc(db, collectionPath('contacts'), contactId);
            await deleteDoc(contactRef);

            // Log the deletion to entity logs
            await logEntityChanges({
                oldObj: contact,
                newObj: null,
                fields: ['firstName', 'lastName', 'email', 'phone'],
                entityType: 'Contact',
                entityId: contactId,
                user: currentUser,
                action: 'Deleted'
            });
            
            // Log to security logs for audit trail
            logEntityDeletion({
                user: currentUser,
                entityType: 'Contact',
                entityId: contactId,
                entityName: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || contactId,
                associations: { customerId: contact.customerId || customerId }
            }).catch(e => console.warn('Failed to log deletion:', e));

            setToast({ message: 'Contact deleted', type: 'success' });
            setShowContactModal(false);
        } catch (err) {
            console.error('Error deleting contact:', err);
            setToast({ message: 'Failed to delete contact', type: 'error' });
        }
    };

    // Notes are now handled by UnifiedNotes component
    // Legacy note loading and add functions removed - see UnifiedNotes

    // Add this function before your return statement:
    const persistNewBusinessType = async (name, setValue) => {
        if (!name.trim()) return;
        const trimmed = name.trim();
        if (!businessTypeOptions.includes(trimmed)) {
            try {
                await addDoc(collection(db, collectionPath('businessType')), {
                    name: trimmed,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser?.uid || null
                });
                setBusinessTypeOptions(prev => [...prev, trimmed]);
            } catch (err) {
                console.error('Failed to persist business type', err);
                setToast({ message: 'Failed to save business type (permission or network error)', type: 'error' });
                return;
            }
        }
        if (setValue) setValue(trimmed);
    };

    // Persist new business focus to Firestore
    const persistNewContactFocus = async (name, setValue) => {
        if (!name.trim()) return;
        const trimmed = name.trim();
        if (!contactFocusOptions.includes(trimmed)) {
            try {
                const focusId = generateContactFocusId(trimmed);
                await setDoc(doc(db, collectionPath('contactFocus'), focusId), {
                    name: trimmed,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser?.uid || null
                });
                setContactFocusOptions(prev => [...prev, trimmed]);
            } catch (err) {
                console.error('Failed to persist contact focus', err);
                setToast({ message: 'Failed to save contact focus (permission or network error)', type: 'error' });
                return;
            }
        }
        if (setValue) setValue(trimmed);
    };

    // Fetch persisted business focus options from Firestore (with error handling)
        useEffect(() => {
        const fetchFocus = async () => {
            try {
                const snap = await getDocs(collection(db, collectionPath('contactFocus')));
                const focus = snap.docs.map(d => d.data().name).filter(Boolean);
                setContactFocusOptions(prev => Array.from(new Set([...prev, ...focus])));
            } catch (err) {
                console.error('Failed to load contact focus options', err);
                setToast({ message: 'Failed to load contact focus options', type: 'error' });
            }
        };
        fetchFocus();
    }, []);

    // Add this helper component inside your file (above CustomerDetailPage):
    function CheckboxDropdown({ options, selected, onChange, onAddNew, showAddNew, setShowAddNew, newValue, setNewValue }) {
        const [open, setOpen] = useState(false);

        const handleToggle = (value) => {
            if (selected.includes(value)) {
                onChange(selected.filter(v => v !== value));
            } else {
                onChange([...selected, value]);
            }
        };

        return (
            <div className="relative">
                <div
                    className="w-full border p-2 rounded bg-white cursor-pointer"
                    onClick={() => setOpen(o => !o)}
                >
                    {selected.length === 0 ? (
                        <span className="text-gray-400">Select Business Focus</span>
                    ) : (
                        <span>{selected.join(', ')}</span>
                    )}
                </div>
                {open && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-y-auto">
                        {options.map(opt => (
                            <label key={opt} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(opt)}
                                    onChange={() => handleToggle(opt)}
                                    className="mr-2"
                                />
                                {opt}
                            </label>
                        ))}
                        {showAddNew && (
                            <div className="mt-2 flex items-center space-x-2">
                                <input
                                    type="text"
                                    className="border p-3 rounded w-full min-w-[250px] text-base"
                                    placeholder="Enter new business type"
                                    value={newValue}
                                    onChange={e => setNewValue(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-indigo-600 text-white rounded text-base"
                                    onClick={onAddNew}
                                >
                                    Add
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded text-base"
                                    onClick={() => {
                                        setShowAddNew(false);
                                        setNewValue('');
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    const handleAddContact = () => {
        setContactModalData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            title: '',
            businessType: '',
            contactFocus: [],
            isPrimary: false,
            customerId: customerId,
        });
        setShowContactModal(true);
    };

    const handleEditCustomer = () => {
        setCustomerModalData(customer);
        setshowCustomerModal(true);
    };

    // Handle opening merge dialog (admin only) - loads all customers for matching
    const handleOpenMergeDialog = async () => {
        setLoadingAllCustomers(true);
        setShowMergeDialog(true);
        try {
            const snapshot = await getDocs(collection(db, collectionPath('customers')));
            const allCusts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setAllCustomers(allCusts);
        } catch (err) {
            console.error('Failed to load customers for merge:', err);
            setToast({ message: 'Failed to load customers for merge', type: 'error' });
            setShowMergeDialog(false);
        } finally {
            setLoadingAllCustomers(false);
        }
    };

    // Handle merge confirmation from dialog
    const handleConfirmMerge = async (sourceCustomer, targetCustomer) => {
        setMerging(true);
        try {
            const results = await mergeCustomers(sourceCustomer, targetCustomer, currentUser);
            
            setToast({
                message: `Successfully merged "${sourceCustomer.customerName}" into "${targetCustomer.customerName}". ` +
                         `Updated ${results.opportunitiesUpdated} opportunities, ${results.ordersUpdated} orders, ` +
                         `${results.contactsUpdated} contacts, and ${results.tasksUpdated} tasks.`,
                type: 'success'
            });
            
            // Close dialog and navigate to the target customer
            setShowMergeDialog(false);
            navigate(`/customer/${targetCustomer.id}`);
        } catch (err) {
            console.error('Merge failed:', err);
            setToast({
                message: `Merge failed: ${err.message}`,
                type: 'error'
            });
        } finally {
            setMerging(false);
        }
    };

    // Load logs for this customer
    useEffect(() => {
        if (!customerId) return;
        setLogsLoading(true);
        const q = query(
            collection(db, collectionPath('logsCustomer')),
            orderBy('timestamp', 'desc'),
            limit(PAGE_SIZE)
        );
        const unsub = onSnapshot(q, snap => {
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Robust filter: match customerId, customerId, entityId, or fallback to string match
            const filtered = all.filter(l => {
                if (!l) return false;
                if (l.customerId === customerId) return true;
                if (l.entityId === customerId) return true;
                if ((l.entityType === 'customer' || l.entityType === 'Customer') && l.entityId === customerId) return true;
                try {
                    const s = JSON.stringify(l);
                    if (s.includes(customerId)) return true;
                } catch (e) {}
                return false;
            });
            setLogs(filtered);
            setLogsLoading(false);
        }, err => {
            console.error('Failed to load logs', err);
            setLogs([]);
            setLogsLoading(false);
        });
        return unsub;
    }, [customerId]);

    // Fetch users related to opportunities (users without VIEW_ALL permission only)
    useEffect(() => {
        if (!currentUser || can(FEATURE_PERMISSIONS.USER_VIEW_ALL)) return; // users with permission already fetch all users
        if (!opportunities.length) return;

        // Get all unique user IDs from opportunities
        const userIds = Array.from(new Set(opportunities.map(opp => opp.uid).filter(Boolean)));

        if (userIds.length === 0) {
            setUsers([]);
            return;
        }

        // Batch get users (max 10 per batch)
        const chunkArray = (arr, n) => {
            const chunks = [];
            for (let i = 0; i < arr.length; i += n) {
                chunks.push(arr.slice(i, i + n));
            }
            return chunks;
        };

        const chunks = chunkArray(userIds, 10);
        Promise.all(
            chunks.map(chunk =>
                getDocs(query(collection(db, collectionPath('users')), where('uid', 'in', chunk)))
            )
        ).then(results => {
            const allUsers = results.flatMap(snap => snap.docs.map(doc => ({ uid: doc.data().uid, ...doc.data() })));
            setUsers(allUsers);
        });
    }, [currentUser, opportunities]);

    // Subscribe to attachments
    useEffect(() => {
        if (!customerId) return;
        const unsub = subscribeToAttachments(customerId, 'customer', setAttachments);
        return () => unsub();
    }, [customerId]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setUploadError('');
        setUploading(true);

        try {
            const willOverwrite = await checkAttachmentExists({
                fileName: file.name,
                entityId: customerId,
                entityType: 'customer'
            });

            if (willOverwrite) {
                const confirm = window.confirm(
                    `A file named "${file.name}" already exists. Uploading will overwrite the existing file. Do you want to continue?`
                );
                if (!confirm) {
                    setUploading(false);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }
            }

            await uploadAttachment({
                file,
                entityId: customerId,
                entityType: 'customer',
                currentUser
            });
            setToast({ type: 'success', message: 'File uploaded successfully.' });

            // Log the upload action to the customer activity log. Try to attach the attachment record if available.
            try {
                const attQ = query(
                    collection(db, collectionPath('attachments')),
                    where('entityId', '==', customerId),
                    where('entityType', '==', 'customer'),
                    where('name', '==', file.name),
                );
                const attSnap = await getDocs(attQ);
                const attDoc = !attSnap.empty ? attSnap.docs[0] : null;
                const attData = attDoc ? { id: attDoc.id, ...attDoc.data() } : null;

                const details = attData
                    ? `File "${file.name}" uploaded (attachment id: ${attData.id}).`
                    : `File "${file.name}" uploaded.`;

                const logId = generateLogId('customer', customerId, 'uploaded_attachment');
                await setDoc(doc(db, collectionPath('logsCustomer'), logId), {
                    customerId: customerId,
                    entityType: 'customer',
                    entityId: customerId,
                    userId: currentUser?.uid || null,
                    userEmail: currentUser?.email || null,
                    action: 'Uploaded Attachment',
                    details,
                    attachmentId: attData?.id || null,
                    attachmentUrl: attData?.url || null,
                    timestamp: serverTimestamp(),
                });
            } catch (logErr) {
                console.warn('Failed to log attachment upload to customer activity log:', logErr);
            }
        } catch (err) {
            setUploadError(err.message || 'Upload failed.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteAttachment = async (attachment) => {
        if (!window.confirm('Delete this file?')) return;
        try {
            await deleteAttachment(attachment);
            setToast({ message: 'Attachment deleted', type: 'success' });

            // Log the delete action to customer activity log (write directly to avoid helper permission edge-cases)
            try {
                const logId = generateLogId('customer', customerId, 'deleted_attachment');
                await setDoc(doc(db, collectionPath('logsCustomer'), logId), {
                    customerId: customerId,
                    entityType: 'customer',
                    entityId: customerId,
                    userId: currentUser?.uid || null,
                    userEmail: currentUser?.email || null,
                    action: 'Deleted Attachment',
                    details: `File "${attachment.name}" deleted.`,
                    attachmentId: attachment.id || null,
                    attachmentUrl: attachment.url || null,
                    timestamp: serverTimestamp(),
                });
            } catch (logErr) {
                console.warn('Failed to log attachment delete to customer activity log:', logErr);
            }
        } catch (err) {
            setToast({ message: 'Failed to delete attachment', type: 'error' });
        }
    };

    useEffect(() => {
        if (!currentUser) return;

        let q = collection(db, collectionPath('customers'));

        if (!can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL)) {
            // If your customer docs have a 'uid' or 'salesRepId' field for assignment:
            q = query(q, where('uid', '==', currentUser.uid));
            // Or, if you use a different field, adjust accordingly:
            // q = query(q, where('salesRepId', '==', currentUser.uid));
        }

        const unsub = onSnapshot(q, (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })));
        });
        return () => unsub();
    }, [currentUser]);

    // Fetch contacts from standalone contacts collection
    useEffect(() => {
        if (!customerId) {
            setContacts([]);
            return;
        }

        // Query contacts collection by customerId
        const contactsQuery = query(
            collection(db, collectionPath('contacts')),
            where('customerId', '==', customerId)
        );

        const unsubContacts = onSnapshot(contactsQuery, (snapshot) => {
            const mappedContacts = snapshot.docs.map((docSnap) => {
                const c = docSnap.data();
                return {
                    id: docSnap.id,
                    contactId: c.contactId || docSnap.id,
                    customerId: c.customerId || customerId,
                    firstName: c.firstName || '',
                    lastName: c.lastName || '',
                    email: c.email || '',
                    phone: c.phone || '',
                    title: c.contactPosition || c.title || '',
                    businessType: c.contactBusinessType || c.businessType || '',
                    contactFocus: Array.isArray(c.contactFocus)
                        ? c.contactFocus
                        : (
                            c.contactFocus ? (
                                typeof c.contactFocus === 'string'
                                    ? c.contactFocus.split(',').map(s => s.trim()).filter(Boolean)
                                    : []
                            )
                            : (
                                Array.isArray(c.contactBusinessFocus) ? c.contactBusinessFocus :
                                typeof c.contactBusinessFocus === 'string' && c.contactBusinessFocus
                                    ? c.contactBusinessFocus.split(',').map(s => s.trim()).filter(Boolean)
                                    : (
                                        Array.isArray(c.businessFocus) ? c.businessFocus :
                                        typeof c.businessFocus === 'string' && c.businessFocus
                                            ? c.businessFocus.split(',').map(s => s.trim()).filter(Boolean)
                                            : (
                                                Array.isArray(c.focus) ? c.focus :
                                                typeof c.focus === 'string' && c.focus
                                                    ? c.focus.split(',').map(s => s.trim()).filter(Boolean)
                                                    : []
                                            )
                                    )
                            )
                        ),
                    isPrimary: c.contactIsPrimary === '1' || c.contactIsPrimary === true || c.isPrimary === '1' || c.isPrimary === true || false,
                    lastUpdate: c.updatedAt || c.lastUpdate || null,
                };
            });
            
            // Sort by isPrimary (primary first), then lastName
            mappedContacts.sort((a, b) => {
                if (a.isPrimary && !b.isPrimary) return -1;
                if (!a.isPrimary && b.isPrimary) return 1;
                return (a.lastName || '').localeCompare(b.lastName || '');
            });
            
            setContacts(mappedContacts);
        }, (err) => {
            console.error('Error fetching contacts from collection:', err);
            setContacts([]);
        });

        return () => unsubContacts();
    }, [customerId]);

    // Customers state is no longer needed for contacts (we're on a customer page)
    // This effect was for fetching customer info from contactCustomerId, which is now unnecessary

    const salesRepOptions = useMemo(() => {
    // All users from Firestore
    const userOptions = users.map(u => ({
        salesRepId: u.uid || u.id,
        salesRepName: u.displayName ||
            ((u.firstName || '') + ' ' + (u.lastName || '')).trim() ||
            u.name || u.email || u.uid || u.id,
        salesRepCustomerId: u.salesRepCustomerId || '',
        salesRepRate: u.salesRepRate || ''
    }));

    // All reps from customer.salesReps (even if not in users)
    const customerRepOptions = Array.isArray(customer?.salesReps)
        ? customer.salesReps.map(rep => ({
            salesRepId: rep.salesRepId || rep.uid || rep.id,
            salesRepName: rep.salesRepName || rep.name || rep.displayName || rep.email || rep.salesRepId || rep.uid || rep.id,
            salesRepCustomerId: rep.salesRepCustomerId || '',
            salesRepRate: rep.salesRepRate || ''
        }))
        : [];

    // Merge and deduplicate by salesRepId
    const all = [...userOptions, ...customerRepOptions];
    const seen = new Set();
    return all.filter(rep => {
        if (!rep.salesRepId || seen.has(rep.salesRepId)) return false;
        seen.add(rep.salesRepId);
        return true;
    });
}, [users, customer]);

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">Loading customer details...</p>
                </div>
            </main>
        );
    }

    if (!customer) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex flex-col items-center justify-center h-64">
                    <p className="text-gray-500 mb-4">Customer not found (ID: {customerId})</p>
                    <button 
                        onClick={() => navigate('/customers')}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Back to Customers
                    </button>
                </div>
            </main>
        );
    }

    const locations = customer?.locations || [];
    const mainLocation = locations.find(
        loc => String(loc.locationIsMainLocation) === '1' || String(loc.locationIsMainLocation) === 'true'
    );
    const useMainLocation = !!mainLocation;
    const otherLocations = locations.filter(loc => String(loc.locationIsMainLocation) !== '1');

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            {/* Modals */}
            {showModal && activeTab === 'opportunities' && (
                <OpportunityModal
                    user={currentUser}
                    initial={editOpportunity}
                    customers={customers}
                    onClose={() => { setShowModal(false); setEditOpportunity(null); }}
                    onSaved={(msg) => { 
                        setShowModal(false); 
                        setEditOpportunity(null); 
                        setToast({ message: msg, type: 'success' }); 
                        setOpportunitiesRefreshKey(prev => prev + 1); // Refresh opportunities list
                        setLogsRefreshKey(prev => prev + 1); // Refresh activity log
                    }}
                    users={users}
                />
            )}

            {showModal && activeTab === 'orders' && (
                <OrderModal
                    user={currentUser}
                    initial={editOrder}
                    customers={customers}
                    onClose={() => { setShowModal(false); setEditOrder(null); }}
                    onSaved={(msg) => { 
                        setShowModal(false); 
                        setEditOrder(null); 
                        setToast({ message: msg, type: 'success' }); 
                        setOrdersRefreshKey(prev => prev + 1); // Refresh orders list
                        setLogsRefreshKey(prev => prev + 1); // Refresh activity log
                    }}
                    salesReps={users}
                />
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/customer')}
                        className="text-indigo-600 hover:text-indigo-500"
                    >
                        ← Back to Customers
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
                </div>                
                {quoting.canBuild && (
                    <button
                        type="button"
                        onClick={() => navigate(`/quotes/new?customerId=${encodeURIComponent(customerId)}`)}
                        title="Create an editable quote draft for this customer"
                        className="rounded bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
                    >
                        Build quote
                    </button>
                )}
            </div>

            {/* customer Info */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
    <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Contact Information</h2>
        <div className="flex items-center gap-2">
            {can(FEATURE_PERMISSIONS.CUSTOMER_EDIT) && (
                <button
                    onClick={handleEditCustomer}
                    className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-500"
                >
                    Edit
                </button>
            )}
            {can(FEATURE_PERMISSIONS.CUSTOMER_MERGE) && (
                <button
                    onClick={handleOpenMergeDialog}
                    disabled={merging || loadingAllCustomers}
                    className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
                    title="Merge this customer into another customer"
                >
                    {merging ? 'Merging...' : loadingAllCustomers ? 'Loading...' : 'Merge'}
                </button>
            )}
        </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
            <div className="mb-2">
                <span className="font-semibold">Customer ID:</span>
                <span className="ml-2 text-indigo-700">
                  {customer.customerAccountNumber
                    ? customer.customerAccountNumber
                    : <span className="text-gray-400">(none)</span>}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">customer Name:</span>
                <span className="ml-2">{customer.customerName}</span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Email:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationEmail || '-'
                        : customer.customerEmail || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Phone:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationPhone || '-'
                        : customer.customerPhone || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Website: </span>
                {customer?.customerWebsite ? (
                    <a href={customer.customerWebsite} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                        {customer.customerWebsite}
                    </a>
                ) : '-'}
            </div>
            <div className="mb-2">
                <span className="font-semibold">Status:</span>
                <span
                    className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.customerStatus === 'Approved'
                            ? 'bg-green-100 text-green-700'
                            : customer.customerStatus === 'Cancelled' || customer.customerStatus === 'Inactive'
                            ? 'bg-gray-100 text-red-700'
                            : customer.customerStatus === 'Pending' || customer.customerStatus === 'Lead'
                            ? 'bg-yellow-100 text-yellow-700'
                            : ''
                    }`}
                >
                    {customer.customerStatus}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Business Type:</span>
                <span className="ml-2">{customer.businessType || '-'}</span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Business Focus:</span>
                <span className="ml-2">
                    {Array.isArray(customer.businessFocus)
                        ? customer.businessFocus.join(', ')
                        : (customer.businessFocus || '-')}
                </span>
            </div>
        </div>
        <div>
            <div className="mb-2">
                <span className="font-semibold">Address 1:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationAddress1 || '-'
                        : customer.customerAddress1 || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Address 2:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationAddress2 || '-'
                        : customer.customerAddress2 || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">City:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationCity || '-'
                        : customer.customerCity || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">State:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationState || '-'
                        : customer.customerState || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Zip:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationZip || '-'
                        : customer.customerZip || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Country:</span>
                <span className="ml-2">
                    {useMainLocation
                        ? mainLocation.locationCountry || '-'
                        : customer.customerCountry || '-'}
                </span>
            </div>
            <div className="mb-2">
                <span className="font-semibold">Assigned Sales Rep:</span>
                <span className="ml-2">
                    {(() => {
                        // Check for single salesRepId first (new format)
                        if (customer.salesRepId) {
                            const user = users.find(u => (u.uid || u.id) === customer.salesRepId);
                            if (user) {
                                return ((user.firstName || '') + ' ' + (user.lastName || '')).trim() || 
                                       user.displayName || 
                                       user.email || 
                                       'Unknown User';
                            }
                            return customer.salesRepId; // fallback to ID if user not found
                        }
                        
                        // Check for legacy salesReps array format
                        if (Array.isArray(customer.salesReps) && customer.salesReps.length > 0) {
                            return customer.salesReps.map(rep =>
                                rep.salesRepName || rep.name || rep.displayName || rep.email || '-'
                            ).join(', ');
                        }
                        
                        return 'Unassigned';
                    })()}
                </span>
            </div>
        </div>
    </div>
</div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8 overflow-x-auto">
                    {showRecommendationsTab && (
                        <button
                            onClick={() => setActiveTab('recommendations')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                activeTab === 'recommendations'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            ✨ Recommendations
                        </button>
                    )}
                    <button
                        onClick={() => setActiveTab('locations')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'locations'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Locations ({locations.length})
                    </button>                    
                    <button
                        onClick={() => setActiveTab('opportunities')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'opportunities'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Opportunities ({opportunities.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('orders')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'orders'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Orders ({orders.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('contacts')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'contacts'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Contacts ({contacts.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'tasks'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Tasks ({tasks.filter(t => t.status === 'open' || t.status === 'Open').length})
                    </button>
                    <button
                        onClick={() => setActiveTab('notes')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'notes'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Notes ({notesCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('attachments')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'attachments'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Attachments ({attachments.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'activity'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Activity Log
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow">
                {activeTab === 'opportunities' ? (
                    <div className="overflow-x-auto">
                        <div className="flex items-center mb-3">                            
                            <span className="mr-2 font-medium text-gray-700">Search:</span>
                            <input
                                type="text"
                                className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                    opportunitiesSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                }`}
                                placeholder="Search opportunities..."
                                value={opportunitiesSearch}
                                onChange={e => setOpportunitiesSearch(e.target.value)}
                            />
                        </div>
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'opportunityNumberLabel') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('opportunityNumberLabel'); setOppSortDir('asc'); }
                                    }}>Opportunity Number {oppSortBy === 'opportunityNumberLabel' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'title') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('title'); setOppSortDir('asc'); }
                                    }}>Opportunity Name {oppSortBy === 'title' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'stage') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('stage'); setOppSortDir('asc'); }
                                    }}>Stage {oppSortBy === 'stage' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'value') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('value'); setOppSortDir('asc'); }
                                    }}>Value {oppSortBy === 'value' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'startDate') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('startDate'); setOppSortDir('asc'); }
                                    }}>Start Date {oppSortBy === 'startDate' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'dueDate') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('dueDate'); setOppSortDir('asc'); }
                                    }}>Due Date {oppSortBy === 'dueDate' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'salesRepId') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('salesRepId'); setOppSortDir('asc'); }
                                    }}>Sales Rep {oppSortBy === 'salesRepId' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'contactName') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('contactName'); setOppSortDir('asc'); }
                                    }}>Contact Name {oppSortBy === 'contactName' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (oppSortBy === 'contactEmail') setOppSortDir(oppSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOppSortBy('contactEmail'); setOppSortDir('asc'); }
                                    }}>Contact Email {oppSortBy === 'contactEmail' && (oppSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {filteredOpportunities.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="text-center py-8 text-gray-500">
                                            No opportunities found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedOpportunities.map(opp => (
                                        <tr key={opp.id} className="text-gray-700">
                                            <td className="px-4 py-3 text-sm">
                                                <Link
                                                    to={`/opportunity/${opp.id}`}
                                                    className="text-indigo-600 hover:underline font-semibold"
                                                >
                                                    {opp.opportunityNumberLabel || '-'}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{opp.leadSource || '-'}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    opp.stage === 'New' ? 'bg-green-100 text-green-700' :
                                                    opp.stage === 'Proposal Sent' ? 'bg-gray-100 text-yellow-700' :
                                                    opp.stage === 'Qualified' ? 'bg-gray-100 text-purple-700' :
                                                    opp.stage === 'Negotiation' ? 'bg-gray-100 text-orange-700' :
                                                    opp.stage === 'Closed Won' ? 'bg-gray-100 text-green-700' :
                                                    opp.stage === 'Closed Lost' ? 'bg-gray-100 text-red-700' :
                                                    'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {opp.stage}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {opp.actualValue 
                                                    ? formatActualValue(opp.actualValue)
                                                    : getValueRangeLabel(opp.value)
                                                }
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {opp.startDate ? formatDate(opp.startDate, currentUser?.dateFormat) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {opp.dueDate ? formatDate(opp.dueDate, currentUser?.dateFormat) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {(() => {
                                                    const rep = users.find(u => u.uid === opp.uid);
                                                    if (!rep) return opp.uid ? opp.uid : 'Unassigned';
                                                    if (rep.firstName || rep.lastName) return `${rep.firstName || ''} ${rep.lastName || ''}`.trim();
                                                    return rep.name || rep.displayName || rep.email || opp.uid || 'Unassigned';
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{opp.contactName || '-'}</td>
                                            <td className="px-4 py-3 text-sm">
                                                {opp.contactEmail ? (
                                                    <a href={`mailto:${opp.contactEmail}`} className="text-indigo-600 hover:underline">
                                                        {opp.contactEmail}
                                                    </a>
                                                ) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{customer?.name || '-'}</td>
                                            <td className="px-4 py-3 text-sm space-x-2">
                                                {can(FEATURE_PERMISSIONS.OPP_EDIT) && (
                                                    <button
                                                        onClick={() => { setEditOpportunity(opp); setShowModal(true); }}
                                                        className="text-indigo-600 hover:underline"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {/* Pagination controls for opportunities */}
                        <PaginationControls
                            currentPage={oppPage}
                            totalPages={oppTotalPages}
                            onPageChange={setOppPage}
                        />
                    </div>
                ) : activeTab === 'orders' ? (
                    <div className="overflow-x-auto">
                        <div className="flex items-center mb-3">

                            <span className="mr-2 font-medium text-gray-700">Search:</span>
                            <input
                                type="text"
                                className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                    ordersSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                }`}
                                placeholder="Search orders..."
                                value={ordersSearch}
                                onChange={e => setOrdersSearch(e.target.value)}
                            />
                        </div>
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (orderSortBy === 'orderNumber') setOrderSortDir(orderSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOrderSortBy('orderNumber'); setOrderSortDir('asc'); }
                                    }}>
                                        Order # {orderSortBy === 'orderNumber' && (orderSortDir === 'desc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (orderSortBy === 'date') setOrderSortDir(orderSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOrderSortBy('date'); setOrderSortDir('asc'); }
                                    }}>
                                        Date {orderSortBy === 'date' && (orderSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (orderSortBy === 'status') setOrderSortDir(orderSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOrderSortBy('status'); setOrderSortDir('asc'); }
                                    }}>
                                        Status {orderSortBy === 'status' && (orderSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (orderSortBy === 'items') setOrderSortDir(orderSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOrderSortBy('items'); setOrderSortDir('asc'); }
                                    }}>
                                        Items {orderSortBy === 'items' && (orderSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (orderSortBy === 'total') setOrderSortDir(orderSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOrderSortBy('total'); setOrderSortDir('asc'); }
                                    }}>
                                        Total {orderSortBy === 'total' && (orderSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (orderSortBy === 'salesRep') setOrderSortDir(orderSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setOrderSortBy('salesRep'); setOrderSortDir('asc'); }
                                    }}>
                                        Sales Rep {orderSortBy === 'salesRep' && (orderSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3">Customer</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {filteredOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-8 text-gray-500">
                                            No orders found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedOrders.map(order => (
                                        <tr key={order.id} className="text-gray-700">
                                            <td className="px-4 py-3 text-sm font-medium">
                                                <Link
                                                    to={`/order/${order.id}`}
                                                    className="text-indigo-600 hover:underline"
                                                >
                                                    {order.orderNumber}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{order.date}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    order.status === 'Open' ? 'bg-blue-100 text-blue-700' :
                                                    order.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                                                    order.status === 'Shipped' ? 'bg-green-100 text-green-700' :
                                                    order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                                                    order.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                                                    'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{order.items}</td>
                                            <td className="px-4 py-3 text-sm">${(order.total || 0).toLocaleString()}</td>
                                            {/* Sales Rep column - use commissions_displayNames or displayName from commissions array */}
                                            <td className="px-4 py-3 text-sm">
                                                {order.salesRep || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{customer?.customerName || '-'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {/* Pagination controls for orders */}
                        <PaginationControls
                            currentPage={orderPage}
                            totalPages={orderTotalPages}
                            onPageChange={setOrderPage}
                        />
                    </div>
                ) : activeTab === 'contacts' ? (
                    // Contacts Tab
                    <div className="overflow-x-auto">
                        <div className="flex items-center mb-3">
                            {/* Add Contact button */}
                            {can(FEATURE_PERMISSIONS.CONTACT_CREATE) && (
                                <button
                                    className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-500 mr-4 whitespace-nowrap"
                                    style={{ fontSize: '0.95rem', lineHeight: '1.5', minWidth: 0 }}
                                    onClick={handleAddContact}
                                    type="button"
                                >
                                    Add Contact
                                </button>
                            )}
                            <span className="mr-2 font-medium text-gray-700">Search:</span>
                            <input
                                type="text"
                                className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                    contactsSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                }`}
                                placeholder="Search contacts..."
                                value={contactsSearch}
                                onChange={e => setContactsSearch(e.target.value)}
                            />
                        </div>
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th style={{ width: '110px' }} className="px-2 py-3 cursor-pointer" onClick={() => {
                                        if (contactSortBy === 'isPrimary') setContactSortDir(contactSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setContactSortBy('isPrimary'); setContactSortDir('asc'); }
                                    }}>Primary Contact{contactSortBy === 'isPrimary' && (contactSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (contactSortBy === 'firstName') setContactSortDir(contactSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setContactSortBy('firstName'); setContactSortDir('asc'); }
                                    }}>Name {contactSortBy === 'firstName' && (contactSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (contactSortBy === 'email') setContactSortDir(contactSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setContactSortBy('email'); setContactSortDir('asc'); }
                                    }}>Email {contactSortBy === 'email' && (contactSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (contactSortBy === 'phone') setContactSortDir(contactSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setContactSortBy('phone'); setContactSortDir('asc'); }
                                    }}>Phone {contactSortBy === 'phone' && (contactSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (contactSortBy === 'title') setContactSortDir(contactSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setContactSortBy('title'); setContactSortDir('asc'); }
                                    }}>Title {contactSortBy === 'title' && (contactSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => {
                                        if (contactSortBy === 'focus') setContactSortDir(contactSortDir === 'asc' ? 'desc' : 'asc');
                                        else { setContactSortBy('focus'); setContactSortDir('asc'); }
                                    }}>Focus {contactSortBy === 'focus' && (contactSortDir === 'asc' ? '▲' : '▼')}</th>
                                    <th className="px-4 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {sortedContacts.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-8 text-gray-500">
                                            No contacts found
                                        </td>
                                    </tr>
                                ) : (
                                    sortedContacts.map(contact => (
                                        <tr key={contact.id} className="text-gray-700">
                                            <td className="px-2 py-3 text-sm text-center">
                                                {contact.isPrimary ? (
                                                    <span className="text-green-600 font-semibold">Yes</span>
                                                ) : (
                                                    <span className="text-gray-400">No</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <Link
                                                    to={`/customer/${customerId}/contact/${contact.id}`}
                                                    className="text-indigo-600 hover:underline"
                                                >
                                                    {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '-'}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{contact.email || '-'}</td>
                                            <td className="px-4 py-3 text-sm">{contact.phone || '-'}</td>
                                            <td className="px-4 py-3 text-sm">{contact.title || '-'}</td>
                                            <td className="px-4 py-3 text-sm">
                                                {Array.isArray(contact.contactFocus)
                                                    ? contact.contactFocus.join(', ')
                                                    : (contact.contactFocus || '-')}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {can(FEATURE_PERMISSIONS.CONTACT_EDIT) && (
                                                    <button
                                                        className="text-indigo-600 hover:underline mr-2"
                                                        onClick={() => {
                                                            // Normalize contactFocus to array before passing to modal
                                                            const cf = contact?.contactFocus;
                                                            const normalizedContact = {
                                                                ...contact,
                                                                contactFocus: Array.isArray(cf)
                                                                    ? cf
                                                                    : (typeof cf === 'string' && cf 
                                                                        ? cf.split(',').map(s => s.trim()).filter(Boolean) 
                                                                        : [])
                                                            };
                                                            setContactModalData(normalizedContact);
                                                            setShowContactModal(true);
                                                        }}
                                                        type="button"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {can(FEATURE_PERMISSIONS.CONTACT_EDIT) && (
                                                    <button
                                                        className="text-green-600 hover:underline mr-2"
                                                        onClick={() => openEmailModal(contact)}
                                                        type="button"
                                                    >
                                                        Email
                                                    </button>
                                                )}
                                                {can(FEATURE_PERMISSIONS.CONTACT_DELETE) && (
                                                    <button
                                                        className="text-red-600 hover:underline"
                                                        onClick={() => handleDeleteContact(contact)}
                                                        type="button"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : activeTab === 'tasks' ? (
                    // Tasks Tab - Related Tasks
                    <div className="p-4">
                        <div className="mb-4 bg-blue-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700">
                                <strong>Related Tasks:</strong> Tasks associated with this customer, its opportunities, orders, or contacts.
                            </p>
                        </div>
                        
                        {tasksLoading ? (
                            <div className="text-center py-8 text-gray-500">Loading tasks...</div>
                        ) : tasks.filter(t => t.status === 'open' || t.status === 'Open').length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No open tasks for this customer.</div>
                        ) : (
                            <div className="space-y-3">
                                {tasks.filter(t => t.status === 'open' || t.status === 'Open').map(task => {
                                    const isPastDue = task.dueDate && new Date(task.dueDate.toDate ? task.dueDate.toDate() : task.dueDate) < new Date() && task.status !== 'completed';
                                    
                                    return (
                                        <div
                                            key={task.id}
                                            className={`bg-white rounded-lg p-4 border-l-4 ${
                                                isPastDue
                                                    ? 'border-red-500'
                                                    : 'border-yellow-500'
                                            } shadow-sm hover:shadow-md transition-shadow`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <Link to={`/tasks/${task.id}`} className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline">
                                                            {task.title}
                                                        </Link>
                                                        <span
                                                            className={`text-xs px-2 py-1 rounded ${
                                                                isPastDue
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                            }`}
                                                        >
                                                            {isPastDue ? 'Overdue' : 'Pending'}
                                                        </span>
                                                    </div>
                                                    {task.description && (
                                                        <p className="text-sm text-gray-600 mt-2">{task.description}</p>
                                                    )}
                                                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                                        <span>
                                                            Due: {task.dueDate ? formatDate(task.dueDate.toDate ? task.dueDate.toDate() : task.dueDate) : 'No date'}
                                                        </span>
                                                        {task.assignedTo && (
                                                            <span>Assigned to: {task.assignedTo}</span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Association badges */}
                                                    {task.associations && Object.entries(task.associations).some(([k, v]) => v && k !== 'customerId' && k !== 'customerName') && (
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            {task.associations.opportunityNumberLabel && (
                                                                <a
                                                                    href={`/opportunities/${task.associations.opportunityId}`}
                                                                    className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200"
                                                                >
                                                                    <span>📊</span>
                                                                    <span>Opportunity #{task.associations.opportunityNumberLabel}</span>
                                                                </a>
                                                            )}
                                                            {task.associations.orderNumber && (
                                                                <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                                                    <span>📦</span>
                                                                    <span>Order #{task.associations.orderNumber}</span>
                                                                </span>
                                                            )}
                                                            {task.associations.contactName && (
                                                                <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                                    <span>👤</span>
                                                                    <span>{task.associations.contactName}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : activeTab === 'notes' ? (
                    // Notes Tab - Using Unified Notes with Cross-Referencing
                    <div className="p-4">
                        <UnifiedNotes 
                            entityType="customer" 
                            entityId={customerId} 
                            maxNotes={100}
                            onNotesLoaded={setNotesCount}
                            refreshTrigger={notesRefreshKey}
                        />
                    </div>
                ) : activeTab === 'activity' ? (
                    // Activity Log Tab - shows logs from ALL related entities
                    <div className="overflow-x-auto">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center flex-1">
                                <span className="mr-2 font-medium text-gray-700">Search:</span>
                                <input
                                    type="text"
                                    className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                        logSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                    }`}
                                    placeholder="Search activity log..."
                                    value={logSearch}
                                    onChange={e => setLogSearch(e.target.value)}
                                />
                            </div>
                            <span className="ml-4 text-sm text-gray-500">
                                {logs.length} entries from all related entities
                            </span>
                        </div>
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Time</th>
                                    <th className="px-4 py-3">Source</th>
                                    <th className="px-4 py-3">User</th>
                                    <th className="px-4 py-3">Action</th>
                                    <th className="px-4 py-3">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-500">No activity found.</td>
                                    </tr>
                                ) : (
                                    paginatedLogs.map(log => {
                                        const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                                        // Get source badge color and label
                                        const sourceConfig = {
                                            customer: { color: 'bg-blue-100 text-blue-800', label: 'Customer' },
                                            Customer: { color: 'bg-blue-100 text-blue-800', label: 'Customer' },
                                            opportunity: { color: 'bg-cyan-100 text-cyan-800', label: 'Opportunity' },
                                            Opportunity: { color: 'bg-cyan-100 text-cyan-800', label: 'Opportunity' },
                                            task: { color: 'bg-emerald-100 text-emerald-800', label: 'Task' },
                                            Task: { color: 'bg-emerald-100 text-emerald-800', label: 'Task' },
                                            order: { color: 'bg-purple-100 text-purple-800', label: 'Order' },
                                            Order: { color: 'bg-purple-100 text-purple-800', label: 'Order' },
                                            contact: { color: 'bg-orange-100 text-orange-800', label: 'Contact' },
                                            Contact: { color: 'bg-orange-100 text-orange-800', label: 'Contact' }
                                        };
                                        const source = sourceConfig[log.entityType] || { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
                                        
                                        return (
                                            <tr key={log.id} className="text-gray-700 hover:bg-gray-50">
                                                <td className="px-4 py-3">{dateObj.toLocaleDateString()}</td>
                                                <td className="px-4 py-3">{dateObj.toLocaleTimeString()}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${source.color}`}>
                                                        {source.label}
                                                        {log._entityLabel && (
                                                            <span className="ml-1 opacity-75">({log._entityLabel})</span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                {(() => {
                                                    const u = users.find(u => u.uid === log.userId || u.email === log.userEmail);
                                                    if (!u) return log.userEmail || log.userId || '-';
                                                    if (u.firstName || u.lastName) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
                                                    return u.displayName || u.name || u.email || log.userEmail || log.userId || '-';
                                                })()}
                                                </td>
                                                <td className="px-4 py-3">{log.action || '-'}</td>
                                                <td className="px-4 py-3">{log.details || '-'}</td>
                                            </tr>
                                        );
                                    })
                            )}
                            </tbody>
                        </table>
                        {totalLogPages >  1 && (
                            <PaginationControls
                                currentPage={logPage}
                                totalPages={totalLogPages}
                                onPageChange={setLogPage}
                            />
                        )}
                    </div>
                ) : null}
            </div>
            {activeTab === 'recommendations' && showRecommendationsTab && (
                <div className="bg-white rounded-lg shadow mt-6">
                    <RecommendationsPanel
                        customerId={customerId}
                        currentUser={currentUser}
                        users={users}
                        can={can}
                        onViewTab={setActiveTab}
                        onToast={setToast}
                    />
                </div>
            )}
            {activeTab === 'attachments' && (
                <div className="bg-white rounded-lg shadow p-4 mt-6">
                   
                    <h3 className="text-lg font-semibold mb-4">Attachments</h3>
                    {can(FEATURE_PERMISSIONS.CUSTOMER_EDIT) && (
                        <div className="mb-4">
                            <label className="px-4 py-2 bg-indigo-600 text-white rounded cursor-pointer hover:bg-indigo-500">
                                {uploading ? 'Uploading...' : 'Upload File'}
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xlsx,.csv"
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                    ref={fileInputRef}
                                />
                            </label>
                            {uploadError && <span className="ml-4 text-red-600">{uploadError}</span>}
                        </div>
                    )}
                    <ul className="divide-y">
                        {attachments.length === 0 ? (
                            <li className="py-4 text-gray-500">No attachments uploaded.</li>
                        ) : (
                            attachments.map(att => {
                                // Find the user who uploaded the file
                                const uploader =
                                    users.find(u => u.uid === att.userId || u.email === att.userEmail);

                                // Get display name or fallback to email/uid
                                const uploaderName =
                                    uploader
                                        ? (uploader.firstName || uploader.lastName
                                            ? `${uploader.firstName || ''} ${uploader.lastName || ''}`.trim()
                                            : uploader.displayName || uploader.name || uploader.email || uploader.uid)
                                        : att.userEmail || att.userId || 'Unknown';

                                const uploadedDate =
                                    att.uploadedAt && att.uploadedAt.toDate
                                        ? att.uploadedAt.toDate().toLocaleString()
                                        : '';

                                return (
                                    <li key={att.id} className="py-2 flex items-center justify-between">
                                        <div>
                                            <a
                                                href={att.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 hover:underline"
                                            >
                                                {att.name}
                                            </a>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <span className="text-xs text-gray-400">
                                                Uploaded: <span className="font-semibold">{uploaderName}</span>
                                                {uploadedDate && ` - ${uploadedDate}`}
                                            </span>
                                            {can(FEATURE_PERMISSIONS.CUSTOMER_EDIT) && (
                                                <button
                                                    className="ml-4 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500 text-xs"
                                                    onClick={() => handleDeleteAttachment(att)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
            {/* Contact Modal for Add/Edit */}
            {showContactModal && (
                <ContactModal
                    open={showContactModal}
                    contact={contactModalData}
                    customers={customers}
                    onClose={() => setShowContactModal(false)}
                    onSave={handleSaveContact}
                    businessTypeOptions={businessTypeOptions}
                    contactFocusOptions={contactFocusOptions}
                    persistNewBusinessType={persistNewBusinessType}
                    persistNewContactFocus={persistNewContactFocus}
                />
            )}
            {showCustomerModal && (
                <CustomerModal
                    user={currentUser}
                    users={users}
                    initial={{
                        ...CustomerModalData,
                        // Pass the salesReps array from the customer object
                        salesReps: Array.isArray(customer?.salesReps) ? customer.salesReps : [],
                    }}
                    onClose={async (updated) => {
                        setshowCustomerModal(false);
                        if (updated) {
                            const customerDoc = await getDoc(doc(db, collectionPath('customers'), customerId));
                            if (customerDoc.exists()) {
                                setCustomer({ id: customerDoc.id, ...customerDoc.data() });
                            }
                            setCustomerRefreshKey(prev => prev + 1); // Refresh customer info
                            setLogsRefreshKey(prev => prev + 1); // Refresh activity log
                        }
                    }}
                />
            )}
            {emailModalOpen && (
                <EmailComposer
                    initialDraft={emailDraft}
                    onClose={() => setEmailModalOpen(false)}
                    onSent={() => setEmailModalOpen(false)}
                    onError={msg => setToast({ message: msg, type: 'error' })}
                />
            )}
            {/* Customer Merge Dialog (admin only) */}
            {showMergeDialog && customer && (
                <CustomerMatchDialog
                    sourceCustomer={customer}
                    allCustomers={allCustomers}
                    onClose={() => setShowMergeDialog(false)}
                    onConfirmMerge={handleConfirmMerge}
                />
            )}
            {activeTab === 'locations' && (
    <div className="p-4">
        {locations.length === 0 && !customer ? (
            <p className="text-gray-500">No locations available.</p>
        ) : (
            <>
                {/* Main Location */}
                <div className="font-semibold text-lg mb-2">Main Location</div>
                <div className="border rounded-lg p-4 bg-blue-50 mb-6">
                    {mainLocation ? (
                        <>
                            <div>
                                <span className="font-semibold">Location ID:</span> {mainLocation.locationId || mainLocation.locationCustomerId || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Store Name:</span> {mainLocation.locationName || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Address 1:</span> {mainLocation.locationAddress1 || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Address 2:</span> {mainLocation.locationAddress2 || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">City:</span> {mainLocation.locationCity || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">State:</span> {mainLocation.locationState || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Zip:</span> {mainLocation.locationZip || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Country:</span> {mainLocation.locationCountry || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Phone:</span> {mainLocation.locationPhone || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Email:</span> {mainLocation.locationEmail || '-'}
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <span className="font-semibold">customer Name:</span> {customer.customerName || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Address 1:</span> {customer.customerAddress1 || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Address 2:</span> {customer.customerAddress2 || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">City:</span> {customer.customerCity || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">State:</span> {customer.customerState || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Zip:</span> {customer.customerZip || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Country:</span> {customer.customerCountry || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Phone:</span> {customer.customerPhone || '-'}
                            </div>
                            <div>
                                <span className="font-semibold">Email:</span> {customer.customerEmail || '-'}
                            </div>
                        </>
                    )}
                </div>
                {/* Other Locations */}
                <div className="font-semibold text-lg mb-2">Other Locations</div>
                {locations.filter(loc => loc !== mainLocation).length === 0 ? (
                    <p className="text-gray-500">No other locations available.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {locations
                            .filter(loc => loc !== mainLocation)
                            .map((loc, idx) => (
                                <div key={idx} className="border rounded-lg p-4 bg-gray-50 mb-4">
                                    <div>
                                        <span className="font-semibold">Location ID:</span> {loc.locationId || loc.locationCustomerId || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Store Name:</span> {loc.locationName || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Address 1:</span> {loc.locationAddress1 || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Address 2:</span> {loc.locationAddress2 || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">City:</span> {loc.locationCity || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">State:</span> {loc.locationState || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Zip:</span> {loc.locationZip || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Country:</span> {loc.locationCountry || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Phone:</span> {loc.locationPhone || '-'}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Email:</span> {loc.locationEmail || '-'}
                                    </div>
                                    {can(FEATURE_PERMISSIONS.CUSTOMER_EDIT) && (
                                        <div className="mt-2">
                                            <button
                                                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-500"
                                                onClick={() => {
                                                    setEditLocation(loc);
                                                    setShowLocationModal(true);
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                )}
            </>
        )}
    </div>
)}
        {showLocationModal && (
          <LocationModal
            open={showLocationModal}
            location={editLocation}
            onClose={() => {
              setShowLocationModal(false);
              setEditLocation(null);
            }}
            onSave={async (updatedLoc) => {
              // Update the location in Firestore/customer object here
              try {
                // Update locations array in Firestore
                const updatedLocations = (customer.locations || []).map(loc =>
                  loc === editLocation ? { ...loc, ...updatedLoc } : loc
                );
                await updateDoc(doc(db, collectionPath('customers'), customerId), {
                  locations: updatedLocations
                });
                
                // Log location update
                await logUserAction({
                  action: 'Location Updated',
                  details: `Location updated for customer: ${updatedLoc.locationAddress1 || ''} ${updatedLoc.locationCity || ''} ${updatedLoc.locationState || ''}`.trim(),
                  entityType: 'Customer',
                  entityId: customerId,
                  customerId: customerId,
                });
                
                
                // Log location update
                await logUserAction({
                  action: 'Location Updated',
                  details: `Location updated for customer: ${updatedLoc.locationAddress1 || ''} ${updatedLoc.locationCity || ''} ${updatedLoc.locationState || ''}`.trim(),
                  entityType: 'Customer',
                  entityId: customerId,
                  customerId: customerId,
                });
                
                setCustomer(c => ({ ...c, locations: updatedLocations }));
                setToast({ message: 'Location updated', type: 'success' });
              } catch (err) {
                setToast({ message: 'Failed to update location', type: 'error' });
              }
              setShowLocationModal(false);
              setEditLocation(null);
            }}
            isEditing={true}
          />
        )}
    </main>
    );
};

export default CustomerDetailPage;
